import { Avatar, Flex, SkeletonText, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import type { BizyPersonPickerNode } from '../../types/nodes';
import { useBizyChatActions } from '../context/actions-context';
import { BizyAnalyticsEvent, useBizyAnalytics } from '../context/analytics-context';
import { useBizyUserResolver } from '../context/user-resolver-context';
import { BIZY_KIT_I18N_NAMESPACE } from '../i18n';
import type { BizyNodeProps } from './node-props';

type Person = BizyPersonPickerNode['payload']['people'][number];

/**
 * Renders the candidate people for an ambiguous name lookup ("Thomas") as
 * one-tap cards under a "People" heading. Clicking a card sends that person's
 * `message` verbatim (via the follow-up send path) so the next turn resolves to
 * exactly one person.
 *
 * The node payload is externalUserId-only — name / avatar / subtitle are
 * resolved through the injected user resolver, mirroring meeting_people_card.
 * Read-only views never send on the viewer's behalf; cards disable while a
 * previous turn is still processing.
 */
export function PersonPickerNode({ node }: BizyNodeProps) {
	const { payload } = node as BizyPersonPickerNode;
	const { t } = useTranslation(BIZY_KIT_I18N_NAMESPACE);
	const { track } = useBizyAnalytics();
	const { fontSize = 'sm', isReadOnly, followUpsDisabled, onSelectFollowUp } = useBizyChatActions();

	if (isReadOnly || !onSelectFollowUp) return null;

	const items = payload.people.filter((person) => person.message.trim().length > 0);
	if (items.length === 0) return null;

	const handleSelect = (person: Person, position: number) => {
		if (followUpsDisabled) return;
		track(BizyAnalyticsEvent.PersonPickerSelected, { position, path: window.location.pathname });
		onSelectFollowUp(person.message);
	};

	return (
		<Flex direction="column" gap={2} mb={4} data-testid="bizy-person-picker">
			<Text fontSize="md" fontWeight="semibold" color="text.default" mb={0.5}>
				{t('personPicker.heading', 'People')}
			</Text>
			{items.map((person, idx) => (
				<PersonCard key={idx} person={person} fontSize={fontSize} isDisabled={followUpsDisabled} onSelect={() => handleSelect(person, idx)} />
			))}
		</Flex>
	);
}

function PersonCard({ person, fontSize, isDisabled, onSelect }: { person: Person; fontSize: 'sm' | 'md'; isDisabled: boolean; onSelect: () => void }) {
	const resolveUser = useBizyUserResolver();
	const resolved = resolveUser(person.externalUserId);
	const displayName = resolved.displayName ?? '';
	const subtitle = resolved.subtitle;
	// Only the name needs the identity fetch; show a skeleton until it resolves
	// so we don't flash the email-bearing `message` string.
	const isLoadingThisRow = resolved.isLoading && !resolved.displayName && !!person.externalUserId;

	return (
		<Flex
			as="button"
			type="button"
			disabled={isDisabled}
			aria-label={displayName || person.message}
			onClick={onSelect}
			align="center"
			gap={3}
			textAlign="left"
			borderWidth="1px"
			borderColor="border.soft"
			borderRadius={12}
			bg="bg.surface"
			px={3}
			py={2.5}
			opacity={isDisabled ? 0.5 : 1}
			cursor={isDisabled ? 'not-allowed' : 'pointer'}
			transition="background 0.15s ease, border-color 0.15s ease"
			{...(!isDisabled && { _hover: { bg: 'bg.subtle', borderColor: 'border.default' } })}
		>
			<Avatar name={displayName} src={resolved.avatarUrl} size="sm" flexShrink={0} />
			<Flex direction="column" flex="1 1 auto" minW={0} gap={0.5}>
				{isLoadingThisRow ? (
					<SkeletonText noOfLines={1} skeletonHeight="3" width="60%" />
				) : (
					<Text fontSize={fontSize} fontWeight="semibold" color="text.default" noOfLines={1}>
						{displayName || person.message}
					</Text>
				)}
				{subtitle && (
					<Text fontSize="xs" color="text.soft" noOfLines={1} lineHeight="short">
						{subtitle}
					</Text>
				)}
			</Flex>
		</Flex>
	);
}
